import { Order, OrderStop, MaterialOrder, MaterialLine, OrderLocation } from '../interfaces/_order.model';
import { OrderHistory } from '../../../src/models/interfaces/_order.model';
import { OrderLists } from '../../../src/models/interfaces/_order-lists.model';

/**
 * HELPER CLASS FOR ORDER PARSING
 */
export class OrderHelper {

    /**
     * MINI-HELPERS: SAME FIELD NAMES (INPUT AND OUTPUT) ON PARSING
     */
    private materialOrderLocationMapperFields = ['sLocaFull', 'sLocaName', 'sAddr', 'sUnit', 'sCity', 'sPost', 'sTimezone'];
    private locationMapperFields = ['sLocaFull', 'sLocaName', 'sAddr', 'sUnit', 'sCity', 'sTimezone', 'nLocaID'];
    private orderMapperFields = ['nSalesOrderID', 'nCustID', 'dtInsertSales', 'sAirbillSeq', 'sSalesOrderCode',
        'sDivFull', 'sCustName', 'sCustFull', 'sRelName', 'sSalesOrderName', 'sInsertSales', 'sDefaultCurrency'];

    /**
     * Related tickets: DEVSERVICE-47065, DEVSERVICE-47856
     *
     * Mapping sAddrUnit to sAddr, we know it's wrong. It was build like this before
     * ideally CT starts sending them separately in this API call so we can remove this
     * for now it's written like this as a fallback to unblock the customer
     * scenario : Create SO via copy order button.
     *
     * @param location location object that will receive the mapping
     * @param locationData location data object that will be used to map the location object
     */

    constructor() { }

    private mapLocationSAddrUnitToSAddr = (location, locationData) => {
        if (locationData && !locationData.sAddr && !locationData.sUnit && locationData.sAddrUnit) {
            location.sAddr = locationData.sAddrUnit;
        }
    };

    /*
     * ORDER SEPARATION
     */
    public simplifyOrders(rawOrderList: any): OrderLists {
        const orderHelper: OrderHelper = new OrderHelper();

        const orderLists: OrderLists = Object.assign({});
        orderLists.activeOrders = [];
        orderLists.finishedOrders = [];

        if (rawOrderList.activeOrders) {
            rawOrderList.activeOrders.forEach((order) => {
                orderLists.activeOrders.push(orderHelper.parseCTOrder(order));
            });
        }

        if (rawOrderList.finishedOrders) {
            rawOrderList.finishedOrders.forEach((order) => {
                orderLists.finishedOrders.push(orderHelper.parseCTOrder(order));
            });
        }

        return orderLists;
    }

    public normalizeLocation = (location) :OrderLocation => {
        if (!location) {
            return null;
        }

        if (!location.sAddr && !location.sUnit && location.sAddrUnit) {
            location.sAddr = location.sAddrUnit;
        }
        if (!location.sCtry && location.sCtryName) {
            location.sCtry = location.sCtryName;
        }
        delete location.sCtryName;

        return location;
    };

    /*
     * ORDER PARSING
     */
    public parseCTOrder(data): Order {

        /*
         * ORDER BASE
         */
        const order: Order = Object.assign({});

        order.bPriceAvailable = !!data && !!data.pricingInfo ? data.pricingInfo.isPriceAvailable : null;
        order.insurance = (!!data && !!data.aCosts) ? data.aCosts.find(c => (c.sCostType === 'INSURANCE')) : null;
        order.sStatus = data ? data.sFullSalesOrderStatus : null;

        this.orderMapperFields.forEach((field) => {
            order[field] = data ? data[field] : null;
        });

        order.sInsertSales = !!data && !!data.sInsertSalesName ? data.sInsertSalesName : order.sInsertSales;

        order.pricing = {
            sTotalVAT: data?.pricingInfo && data.sFullSalesOrderStatus !== 'OM_INCOMPLETE'
                ? data.pricingInfo.totalVAT
                : null,
            sTotalPriceExcl: data?.pricingInfo && data.sFullSalesOrderStatus !== 'OM_INCOMPLETE'
                ? data.pricingInfo.totalPriceExcl
                : null,
            sTotalPriceIncl: data?.pricingInfo && data.sFullSalesOrderStatus !== 'OM_INCOMPLETE'
                ? data.pricingInfo.totalPriceIncl
                : null,
            fTotalPriceIncl: data?.pricingInfo && data.sFullSalesOrderStatus !== 'OM_INCOMPLETE'
                ? data.pricingInfo.totalPriceInclValue
                : null,
            fTotalPriceExcl: data?.pricingInfo && data.sFullSalesOrderStatus !== 'OM_INCOMPLETE'
                ? data.pricingInfo.totalPriceExclValue
                : null
        };

        order.milesActivity = data.aMiles ? data.aMiles : [];

        order.stops = (!!data && !!data.aStops) ? this.parseCTStop(data) : [];
        order.statusHistory = !!data && !!data.aHistory ? this.parseCTEventHistory(data.aHistory) : null;
        order.materialOrders = !!data && !!data.aMatOrders ? this.parseCTSimpleMaterialOrders(data.aMatOrders) : null;

        order.fFinalLmeter = !!data && !!data.fFinalLmeter ? data.fFinalLmeter : null;

        order.collectionTime = [];
        order.collectionContact = [];
        order.deliveryTime = [];
        order.deliveryContact = [];
        order.collectionLocation = [];
        order.deliveryLocation = [];

        order.jRecommendedDepartPortLoca = this.normalizeLocation(order.jRecommendedDepartPortLoca);
        order.jRecommendedArrivePortLoca = this.normalizeLocation(order.jRecommendedArrivePortLoca);

        order.stops.forEach(stop => {

            if (stop.collectionMatOrders[0]) {
                const matOrderId = stop.collectionMatOrders[0].nMatOrderID;
                const matOrder = data.aMatOrders.find(mo => mo.nMatOrderID === matOrderId);

                order.collectionLocation.push({
                    sPost: matOrder.jOrigLoca.sPost,
                    sCtry: matOrder.jOrigLoca.sCtryName,
                    sCtryCode: matOrder.jOrigLoca.sCtryCode,
                    fLat: matOrder.jOrigLoca.aLatLon[0],
                    fLong: matOrder.jOrigLoca.aLatLon[1],
                } as OrderLocation);


                /**
                 * COLLECTION TIME
                 */

                order.collectionTime.push({
                    dtFrom: matOrder.dtCollSchedlFra,
                    dtUntil: matOrder.dtCollSchedlTil,
                    bAsap: !(matOrder && matOrder.dtCollSchedlFra)
                        || !(matOrder && matOrder.dtCollSchedlTil)
                });

                /**
                 * COLLECTION CONTACT
                 */

                order.collectionContact.push({
                    sName: matOrder.jCollContact ? matOrder.jCollContact.name : null,
                    sPhone: matOrder.jCollContact ? matOrder.jCollContact.phone : null,
                    sEmail: matOrder.jCollContact ? matOrder.jCollContact.email : null
                });

                const c = order.collectionLocation[order.collectionLocation.length - 1];
                this.locationMapperFields.forEach((field) => {
                    c[field] = matOrder.jOrigLoca[field];
                });

                this.mapLocationSAddrUnitToSAddr(c, matOrder.jOrigLoca);
            }

            if (stop.deliveryMatOrders[0]) {
                const matOrderId = stop.deliveryMatOrders[0].nMatOrderID;
                const matOrder = data.aMatOrders.find(mo => mo.nMatOrderID === matOrderId);

                order.deliveryLocation.push({
                    sPost: matOrder.jDestLoca.sPost,
                    sCtry: matOrder.jDestLoca.sCtryName,
                    sCtryCode: matOrder.jDestLoca.sCtryCode,
                    fLat: matOrder.jDestLoca.aLatLon[0],
                    fLong: matOrder.jDestLoca.aLatLon[1],
                } as OrderLocation);

                /**
                 * DELIVERY TIME
                 */

                order.deliveryTime.push({
                    dtFrom: matOrder.dtDeliSchedlFra,
                    dtUntil: matOrder.dtDeliSchedlTil,
                    bAsap: !(matOrder && matOrder.dtDeliSchedlFra)
                        || !(matOrder && matOrder.dtDeliSchedlTil)
                });

                /**
                 * DELIVERY CONTACT
                 */

                order.deliveryContact.push({
                    sName: matOrder.jDeliContact ? matOrder.jDeliContact.name : null,
                    sPhone: matOrder.jDeliContact ? matOrder.jDeliContact.phone : null,
                    sEmail: matOrder.jDeliContact ? matOrder.jDeliContact.email : null
                });

                const d = order.deliveryLocation[order.deliveryLocation.length - 1];
                this.locationMapperFields.forEach((field) => {
                    d[field] = matOrder.jDestLoca[field];
                });

                this.mapLocationSAddrUnitToSAddr(d, matOrder.jDestLoca);
            }

        });

        return order;
    }

    private parseCTSimpleMaterialOrders(matOrders: any[]) {
        const simpleMaterialOrders: MaterialOrder[] = [];

        if (matOrders) {
            matOrders.forEach((matOrder) => {
                simpleMaterialOrders.push(this.parseMaterialOrder(matOrder, null));
            });
        }

        return simpleMaterialOrders;
    }

    private parseCTEventHistory(history: any[]): OrderHistory[] {
        const historyList: OrderHistory[] = [];

        if (history) {
            history.forEach((event) => {
                const newEvent: OrderHistory = Object.assign({});
                newEvent.sEventCode = event.sEventCode;
                newEvent.sRelName = event.sRelName;
                newEvent.sRelType = event.sRelType;
                newEvent.podURL = event.podURL;
                newEvent.pocURL = event.pocURL;
                newEvent.dtInsert = event.dtInsert;
                newEvent.actual = event.actual;
                newEvent.recipient = event.recipient;

                historyList.push(newEvent);
            });
        }

        return historyList;
    }

    /**
     * ORDER STOP PARSING
     */
    private parseCTStop(data): OrderStop[] {

        /* data.stops.forEach(stop => {
            stop.nStopId
            const matOrderIds = data.aMiles.filter(milestone => {
                milestone.nStopId === stop.nStopId && (milestone.type === 'COLLECT' || milestone.type === 'DELIVER')
            }).map(milestone => milestone.nMatOrder);

            const matOrders = data.matOrders.filter(matOrder => matOrderIds.contains(matOrder.id));
            if (stop.stopType === 'COLLECT') {
                stop.collectMatOrders = matOrders
            } else {
                stop.deliveryMatOrders = matOrders
            }
        }); */

        const newStops: OrderStop[] = [];

        data.aStops.forEach((stop, index) => {
            const newStop: OrderStop = Object.assign({});
            newStop.nStopSeq = index;

            newStop.location = Object.assign({});
            newStop.location.sPost = stop.jLoca ? stop.jLoca.sPost : null;
            newStop.location.sCtry = stop.jLoca ? stop.jLoca.sCtryName : null;
            newStop.location.fLat = stop.jLoca ? stop.jLoca.aLatLon[0] : null;
            newStop.location.fLong = stop.jLoca ? stop.jLoca.aLatLon[1] : null;
            newStop.stopID = stop.nStopID;

            this.locationMapperFields.forEach((field) => {
                newStop.location[field] = stop.jLoca ? stop.jLoca[field] : null;
            });

            this.mapLocationSAddrUnitToSAddr(newStop.location, stop.jLoca);

            newStop.collectionMatOrders = this.parseStopMaterialOrderList(stop.nStopID, 'COLLECT', data);
            newStop.deliveryMatOrders = this.parseStopMaterialOrderList(stop.nStopID, 'DELIVER', data);

            newStop.dtArriveSched = stop.oArriMile ? stop.oArriMile.dtSchedlFra : null;
            newStop.dtArriveActual = stop.oArriMile ? stop.oArriMile.dtActual : null;
            newStop.dtDepartSched = stop.oDepaMile ? stop.oDepaMile.dtSchedlTil : null;
            newStop.dtDepartActual = stop.oDepaMile ? stop.oDepaMile.dtActual : null;
            newStop.sStopType = stop.sStopType;

            newStops.push(newStop);
        });

        return newStops;

    }

    /**
     * STOP MATERIAL ORDER LIST
     */
    private parseStopMaterialOrderList(nStopID, type, data): MaterialOrder[] {
        const matData: MaterialOrder[] = [];

        if (data.aMiles) {
            data.aMiles.forEach((mile) => {
                if (mile.nStopID === nStopID && mile.sMileType === type) {
                    matData.push(this.parseStopMaterialOrdersByID(data.aMatOrders, type, mile.nMatOrderID));
                }
            });
        }

        return matData;
    }

    /**
     * MATERIAL ORDER PARSE BY ID
     */
    private parseStopMaterialOrdersByID(aMatOrders, type, nMatOrderID): MaterialOrder {

        let matData: MaterialOrder = null;

        if (aMatOrders) {
            matData = aMatOrders.find(matOrder => matOrder.nMatOrderID === nMatOrderID);
        }

        return (matData) ? this.parseMaterialOrder(matData, type) : Object.assign({});

    }

    /**
     * MATERIAL ORDER PARSING
     */
    private parseMaterialOrder(matOrder, type): MaterialOrder {

        const materialOrder: MaterialOrder = Object.assign({});

        materialOrder.nMatOrderID = matOrder.nMatOrderID;
        materialOrder.type = type;
        materialOrder.sMatOrderCode = matOrder.sMatOrderCode;
        materialOrder.sMatOrderStatus = matOrder.sMatOrderStatus;

        materialOrder.sDeliOntimeReasonCode = matOrder.sDeliOntimeReasonCode;
        materialOrder.sDeliOntimeReasonName = matOrder.sDeliOntimeReasonName;
        materialOrder.sDeliSchedlChangeReasonCode = matOrder.sDeliSchedlChangeReasonCode;
        materialOrder.sDeliSchedlChangeReasonName = matOrder.sDeliSchedlChangeReasonName;

        materialOrder.sCollOntimeReasonCode = matOrder.sCollOntimeReasonCode;
        materialOrder.sCollOntimeReasonName = matOrder.sCollOntimeReasonName;
        materialOrder.sCollSchedlChangeReasonCode = matOrder.sCollSchedlChangeReasonCode;
        materialOrder.sCollSchedlChangeReasonName = matOrder.sCollSchedlChangeReasonName;

        materialOrder.sInstructions = (type === 'COLLECT') ? matOrder.sInstrColl : matOrder.sInstrDeli;
        materialOrder.dtSchedFra = (type === 'COLLECT') ? matOrder.dtCollSchedlFra : matOrder.dtDeliSchedlFra;
        materialOrder.dtSchedTil = (type === 'COLLECT') ? matOrder.dtCollSchedlTil : matOrder.dtDeliSchedlTil;
        materialOrder.dtActual = (type === 'COLLECT') ? matOrder.dtCollActual : matOrder.dtDeliActual;

        materialOrder.nETACollectDelay = matOrder.nETACollectDelay;
        materialOrder.sETACollectStatus = matOrder.sETACollectStatus;
        materialOrder.nETADeliverDelay = matOrder.nETADeliverDelay;
        materialOrder.sETADeliverStatus = matOrder.sETADeliverStatus;

        materialOrder.sIncoTermCode = matOrder.sIncoTermCode;
        materialOrder.sIncoTermLocation = matOrder.sIncoTermLocation;

        materialOrder.fLat = (type === 'COLLECT')
            ? ((matOrder.jOrigLoca) ? matOrder.jOrigLoca.aLatLon[0] : null)
            : ((matOrder.jDestLoca) ? matOrder.jDestLoca.aLatLon[0] : null);
        materialOrder.fLong = (type === 'COLLECT')
            ? ((matOrder.jOrigLoca) ? matOrder.jOrigLoca.aLatLon[1] : null)
            : ((matOrder.jDestLoca) ? matOrder.jDestLoca.aLatLon[1] : null);
        materialOrder.sCtry = (type === 'COLLECT')
            ? ((matOrder.jOrigLoca) ? matOrder.jOrigLoca.sCtryName : null)
            : ((matOrder.jDestLoca) ? matOrder.jDestLoca.sCtryName : null);

        this.materialOrderLocationMapperFields.forEach((field) => {
            materialOrder[field] = (type === 'COLLECT')
                ? ((matOrder.jOrigLoca) ? matOrder.jOrigLoca[field] : null)
                : ((matOrder.jDestLoca) ? matOrder.jDestLoca[field] : null);
        });

        if (type === 'COLLECT') {
            this.mapLocationSAddrUnitToSAddr(materialOrder, matOrder.jOrigLoca);
        } else {
            this.mapLocationSAddrUnitToSAddr(materialOrder, matOrder.jDestLoca);
        }

        materialOrder.contact = Object.assign({});
        materialOrder.contact.sName = materialOrder.fLat = (type === 'COLLECT')
            ? ((matOrder.jCollContact) ? matOrder.jCollContact.name : null)
            : ((matOrder.jDeliContact) ? matOrder.jDeliContact.name : null);
        materialOrder.contact.sPhone = materialOrder.fLat = (type === 'COLLECT')
            ? ((matOrder.jCollContact) ? matOrder.jCollContact.phone : null)
            : ((matOrder.jDeliContact) ? matOrder.jDeliContact.phone : null);
        materialOrder.contact.sEmail = materialOrder.fLat = (type === 'COLLECT')
            ? ((matOrder.jCollContact) ? matOrder.jCollContact.email : null)
            : ((matOrder.jDeliContact) ? matOrder.jDeliContact.email : null);

        materialOrder.lines = this.parseMaterialLines(matOrder.aLines, matOrder);

        return materialOrder;

    }

    /**
     * MATERIAL LINE PARSING
     */
    private parseMaterialLines(lines, matOrder): MaterialLine[] {

        const newLines: MaterialLine[] = [];

        if (lines) {
            lines.forEach((line) => {

                const specials = [];
                if (line.bHazard) { specials.push('Hazard'); }
                if (line.bHeavy) { specials.push('Heavy'); }
                if (line.bPallet) { specials.push('Pallet'); }
                if (line.bReturn) { specials.push('Return'); }
                if (line.bSuperHeavy) { specials.push('Super-Heavy'); }

                const newLine: MaterialLine = Object.assign({});
                newLine.nMatLineID = line.nMatLineID;
                newLine.nQuantity = line.nQtyRequ;
                newLine.sDimension = (line.sDimensions) ? line.sDimensions : null;
                newLine.sMass = (line.fMassGro) ? `${line.fMassGro} ${line.sMassUnit}` : null;
                newLine.fMass = line.fMassGro;
                newLine.sAmount = (line.fAmount) ? `${line.fAmount} ${line.sCurrCode}` : null;
                newLine.sSpecials = specials.join(', ');
                newLine.sContentDescription = (line.sContentDescription) ? line.sContentDescription : null;

                newLine.jSpecials = Object.assign({});
                newLine.jSpecials.bHazard = line.bHazard;
                newLine.jSpecials.bHeavy = line.bHeavy;
                newLine.jSpecials.bPallet = line.bPallet;
                newLine.jSpecials.bReturn = line.bReturn;
                newLine.jSpecials.bSuperHeavy = line.bHazbSuperHeavyard;

                newLine.fWidth = line.fWidth;
                newLine.fHeight = line.fHeight;
                newLine.fLength = line.fLength;
                newLine.fVolume = line.fVolume;
                newLine.fAmount = line.fAmount;
                newLine.sCurrCode = line.sCurrCode;
                newLine.sShippingUnit = line.sShippingUnit;
                newLine.bHazard = line.bHazard;
                newLine.bHeavy = line.bHeavy;
                newLine.bIsPartWithAssets = line.bIsPartWithAssets;
                newLine.bPartSwap = line.bPartSwap;
                newLine.bReturn = line.bReturn;
                newLine.bStackAll = line.bStackAll;
                newLine.bSuperHeavy = line.bSuperHeavy;
                newLine.bTemper = line.bTemper;
                newLine.sMatOrderCode = line.sMatOrderCode;
                newLine.sPartFull = line.sPartFull;
                newLine.nPartID = line.nPartID;
                newLine.fFinalLmeter = matOrder.fFinalLmeter;

                newLine.aMdHandlingIDs = line.aMdHandlingIDs;
                newLine.aMdHandlingNames = line.aMdHandlingNames;
                newLine.aQuestionAnswers = line.aQuestionAnswers;
                newLine.nQuestionnaireID = line.nQuestionnaireID;

                newLines.push(newLine);

            });
        }

        return newLines;

    }
}
